/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type TaskOptionsArgs, TaskService } from 'app/common/services';
import { type CIFSClient, type FileSystem } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type CreateCifsParams } from '../share';

export interface AddCifsClientParams {
    auth?: CIFSClient[];
    cifsID?: string;
    vstoreId?: string;
}

@Injectable({ providedIn: 'root' })
export class CifsShareService {
    constructor(
        private http: HttpService,
        private taskService: TaskService,
    ) { }

    getBatchRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('CIFSShare.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    countRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('CIFSShare.count', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    getBatch(fsID: number | string, dtreeID: number | string, vstoreId: string, range: number[], options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(vstoreId)
            .setParams({
                range: `[${range[0]}-${range[1]}]`,
            })
            .setFilters([
                { field: 'FSID', matchMode: '::', value: fsID },
                { field: 'DTREEID', matchMode: '::', value: dtreeID },
            ]);
        return this.http.get(rest, options);
    }

    getCount(fsID: number | string, dtreeID: number | string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.countRest(vstoreId).setFilters([
            { field: 'FSID', matchMode: '::', value: fsID },
            { field: 'DTREEID', matchMode: '::', value: dtreeID },
        ]);
        return this.http.get(rest, options);
    }

    get(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSShare.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options);
    }

    create(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSShare.post');
        return this.http.post(rest, params, options);
    }

    createByOmtask(objName: string, params: CreateCifsParams, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('cifsShare.post', 'V2');
        return this.taskService.post('createCifsShare', objName, rest, params, options);
    }

    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSShare.put').format(id);
        return this.http.put(rest, params, options);
    }

    delete(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSShare.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    getBatchFSRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    getBatchFSCountRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.count');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 查询单个dtree
     */
    getDtree(dtreeId: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('dtree.get').format(dtreeId);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options);
    }

    getBatchDtreeRest(fileSystem?: FileSystem): Rest {
        const rest = this.http.makeUrl('dtree.getBatch');
        if (fileSystem) {
            rest.setParams({
                PARENTID: fileSystem.ID,
                vstoreId: fileSystem.vstoreId,
            });
        }
        return rest;
    }

    /**
     * 查询CIFS服务的设置
     */
    getCIFSService(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('CIFSService.get');
        return this.http.get(rest, options);
    }

    /**
     * 查询存储池 列表
     */
    getAllPools(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('storagePool.getBatch');
        return this.http.get(rest, options);
    }

    /**
     * 获取WINDOWS用户rest
     */
    getWindowsUserRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalUser.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取WINDOWS用户组rest
     */
    getWindowsUserGroupRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('WindowsLocalGroup.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取UNIX用户rest
     */
    getUnixUserRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('UnixLocalUser.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 获取UNIX用户组rest
     */
    getUnixUserGroupRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('UnixLocalGroup.getBatch');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * cifs共享名校验
     */
    cifsShareNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }

        // 不允许前后有空格或全空格的名称
        const trimValue = control.value.trim();
        if (!trimValue || trimValue.length !== control.value.length) {
            return { cifsShareName: true };
        }

        const invalidReg = /["\/\\\[\]:|<>\+;,\?\*=\t]+/;
        const reservedNameArr = ['ipc$', 'autohome', '~', 'print$'];
        const valid = !(invalidReg.test(control.value) || reservedNameArr.includes(control.value.toLocaleLowerCase()));
        return valid ? null : { cifsShareName: true };
    }

    /**
     * cifs homedir 共享名校验
     */
    homedirShareNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }

        // 不允许前后有空格或全空格的名称
        const trimValue = control.value.trim();
        if (!trimValue || trimValue.length !== control.value.length) {
            return { homedirShareName: true };
        }

        const invalidReg = /["\/\\\[\]:|<>\+;,\?\*=\t]+/;
        const reservedNameArr = ['ipc$', '~', 'print$'];
        const valid = !(invalidReg.test(control.value) || reservedNameArr.includes(control.value.toLocaleLowerCase()));
        return valid ? null : { homedirShareName: true };
    }

    /**
     * Homedir相对路径校验
     */
    homedirPathValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (!value) {
            return null;
        }
        // 1、不能出现连续的两个/
        if (value.indexOf('//') !== -1) {
            return { homedirPath: true };
        }
        // 2、%d %w只能出现一次
        if (value.split('%w').length - 1 > 1 || value.split('%d').length - 1 > 1) {
            return { homedirPath: true };
        }
        // 3、不包含":*\\?\"<>|"中任意字符
        const reg = /["\\:|<>\?\*]+/;
        const valid = !(reg.test(value));
        return valid ? null : { homedirPath: true };
    }

    /**
     * AD域用户/用户组名称校验
     */
    cifsAdNameValidator(control: AbstractControl): ValidationErrors | null {
        const reg = /[^ \f\n\r\t\v\\]+\\[^ \f\n\r\t\v\\]+/;
        const valid = reg.test(control.value);
        return valid ? null : { adName: true };
    }

    /**
     * 获取对应租户CIFS智能推荐数据
     */
    getSmartguiCIFS(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartgui.getCifsshare', 'V2').format(vstoreId);
        return this.http.get(rest, options);
    }

    /**
     * 查询对应名称的文件系统
     */
    getFSOnName(params: FileSystem, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('fileSystem.getBatch')
            .setFilters([
                { field: 'NAME', matchMode: '::', value: params.name },
                { field: 'vstoreId', matchMode: '::', value: params.vstoreId },
            ]);
        return this.http.get(rest, options);
    }

    /**
     * 查询单个文件系统
     * @param id
     */
    getFileSystem(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }
}
